import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/useToast";
import { ASSISTANT_CREATION_PROMPT } from "@/lib";
import { ChatCompletionService } from "@/services/openai/chatCompletionService";
import { ArrowUp, Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { z } from "zod";

// Define schema for OpenAI structured output
const AssistantDetailsSchema = z.object({
  name: z
    .string()
    .describe(
      "A concise, descriptive name (NEVER use the word 'Assistant' - use titles like Specialist, Expert, Advisor, Analyst, etc.)"
    ),
  description: z
    .string()
    .describe(
      "A brief description of the assistant's purpose and capabilities"
    ),
  instructions: z
    .string()
    .describe(
      "Comprehensive system instructions in markdown format for the assistant"
    ),
  recommended_tools: z
    .array(z.string())
    .describe("Names of tools that would be useful for this assistant"),
  reasoning: z
    .string()
    .describe("Brief explanation for why these tools were selected"),
  primary_purpose: z
    .string()
    .describe(
      "The primary purpose of the assistant (creative, coding, writing, research, analysis, etc.)"
    ),
  recommended_model: z
    .string()
    .describe("The recommended model for this assistant based on its purpose"),
});

// Helper function to select the best model based on purpose
const selectModelForPurpose = (purpose) => {
  purpose = purpose.toLowerCase();

  // Creative tasks - OpenAI models
  if (
    purpose.includes("creative") ||
    purpose.includes("image") ||
    purpose.includes("design") ||
    purpose.includes("generate") ||
    purpose.includes("art")
  ) {
    return "gpt-4o";
  }

  // Coding tasks - Claude or DeepSeek models
  if (
    purpose.includes("code") ||
    purpose.includes("programming") ||
    purpose.includes("developer") ||
    purpose.includes("software") ||
    purpose.includes("technical")
  ) {
    return "claude-sonnet-4-20250514"; // Claude 4 Sonnet is excellent for code
  }

  // Writing tasks - Gemini models
  if (
    purpose.includes("writing") ||
    purpose.includes("content") ||
    purpose.includes("blog") ||
    purpose.includes("article") ||
    purpose.includes("text")
  ) {
    return "gemini-2.5-pro"; // Gemini excels at writing tasks
  }

  // Research and analysis - Mistral/Groq
  if (
    purpose.includes("research") ||
    purpose.includes("analysis") ||
    purpose.includes("data") ||
    purpose.includes("information") ||
    purpose.includes("search")
  ) {
    return "mistral-small-latest"; // Good for research tasks
  }

  // Default to OpenAI's general purpose model
  return "gpt-4.1-mini";
};

export const AssistantCreationChat = ({
  onAssistantGenerated,
  onStreamingUpdate,
  onCancel,
}) => {
  const [userQuery, setUserQuery] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const abortControllerRef = useRef(null);
  const hasTriggeredEditModeRef = useRef(false);

  const generateAssistant = async () => {
    if (!userQuery.trim()) {
      toast({
        title: "Empty query",
        description: "Please describe what kind of assistant you want.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    hasTriggeredEditModeRef.current = false;
    abortControllerRef.current = new AbortController();

    try {
      const chatCompletionService = new ChatCompletionService();

      // Use the better system prompt from InstructionsGeneratorPopup.jsx
      chatCompletionService.setSystemPrompt(ASSISTANT_CREATION_PROMPT);

      // Create user message that prompts for assistant details
      const userMessage = `Create an assistant based on this request: ${userQuery}

Think step by step about what kind of assistant would be most helpful for this request:
1. Determine the primary purpose (creative, coding, writing, research, general)
2. Select the most appropriate tools for this purpose
3. Create comprehensive markdown-formatted instructions following the provided format
4. Recommend the best model for this type of assistant

CRITICAL NAMING REQUIREMENT: The name must NEVER contain the word "Assistant". Use professional titles like Specialist, Expert, Advisor, Analyst, Consultant, Manager, etc. instead.

Examples of good names: "Research Specialist", "Code Expert", "Financial Advisor", "Content Creator"
Examples of bad names: "Research Assistant", "Coding Assistant", "AI Assistant"

INSTRUCTION FORMAT REQUIREMENT: Use markdown format with clear sections like **Core Responsibilities**, **Methodology**, **Output Requirements**, **Quality Standards**, and **Guidelines**.

Analyze the request carefully to determine if the assistant is primarily for creative tasks, coding, writing, research, or general assistance.`;

      // Get personal workspace ID from auth store ahead of time
      const { useAuthStore } = await import("@/store/authStore");
      const authStore = useAuthStore.getState();
      const personalWorkspaceId = authStore.getPersonalWorkspaceId();

      // Use streaming if callback is provided
      if (onStreamingUpdate) {
        await chatCompletionService.createStreamingJsonCompletion(
          [{ role: "user", content: userMessage }],
          {
            model: "gpt-4.1-mini",
            featureId: "assistant_generation",
            signal: abortControllerRef.current.signal,
            onFieldUpdate: (fields, isComplete) => {
              // Once we have a name, trigger edit mode and start streaming updates
              if (fields.name && !hasTriggeredEditModeRef.current) {
                hasTriggeredEditModeRef.current = true;
                // Signal to switch to edit mode with initial name
                onStreamingUpdate({
                  type: "switch_to_edit",
                  name: fields.name,
                  prompt: userQuery,
                });
              }

              // Send streaming updates for description and instructions
              if (hasTriggeredEditModeRef.current) {
                onStreamingUpdate({
                  type: "field_update",
                  fields: {
                    name: fields.name,
                    description: fields.description || "",
                    instructions: fields.instructions || "",
                  },
                  isComplete,
                });
              }
            },
            onComplete: (finalData) => {
              // Validate and finalize the data
              try {
                const validatedData = AssistantDetailsSchema.parse(finalData);

                // Determine the best model to use
                const recommendedModel =
                  validatedData.recommended_model ||
                  selectModelForPurpose(validatedData.primary_purpose);

                // Filter tools to only include allowed ones
                const allowedTools = ["web_search", "image_gen"];
                const filteredTools = validatedData.recommended_tools.filter(
                  (tool) => allowedTools.includes(tool)
                );

                // Ensure we always have at least web_search
                if (!filteredTools.includes("web_search")) {
                  filteredTools.unshift("web_search");
                }

                // Pass the final generated data
                onAssistantGenerated({
                  name: validatedData.name,
                  description: validatedData.description,
                  instructions: validatedData.instructions,
                  tools: filteredTools,
                  reasoning: validatedData.reasoning,
                  prompt: userQuery,
                  model: recommendedModel,
                  primary_purpose: validatedData.primary_purpose,
                  workspace_id: personalWorkspaceId,
                });
              } catch (validationError) {
                console.error("Validation error:", validationError);
                // Use whatever data we have
                onAssistantGenerated({
                  name: finalData.name || "New Specialist",
                  description: finalData.description || "",
                  instructions: finalData.instructions || "",
                  tools: ["web_search"],
                  reasoning: finalData.reasoning || "",
                  prompt: userQuery,
                  model:
                    finalData.recommended_model ||
                    selectModelForPurpose(finalData.primary_purpose || "general"),
                  primary_purpose: finalData.primary_purpose || "general",
                  workspace_id: personalWorkspaceId,
                });
              }
            },
            onError: (error) => {
              console.error("Streaming error:", error);
              if (error.name !== "AbortError") {
                toast({
                  title: "Generation Failed",
                  description:
                    error.message ||
                    "Failed to generate assistant. Please try again.",
                  variant: "destructive",
                });
              }
            },
          }
        );
      } else {
        // Fallback to non-streaming for backward compatibility
        const response =
          await chatCompletionService.createChatCompletionWithStructuredOutput(
            [{ role: "user", content: userMessage }],
            {
              model: "gpt-4.1-mini",
              response_format: { type: "json_object" },
              featureId: "assistant_generation",
            }
          );

        // Extract the response content and parse the JSON
        const content = response.choices[0].message.content;

        // Parse the structured output
        const parsedData = JSON.parse(content.trim());
        const validatedData = AssistantDetailsSchema.parse(parsedData);

        // Determine the best model to use
        const recommendedModel =
          validatedData.recommended_model ||
          selectModelForPurpose(validatedData.primary_purpose);

        // Filter tools to only include allowed ones
        const allowedTools = ["web_search", "image_gen"];
        const filteredTools = validatedData.recommended_tools.filter((tool) =>
          allowedTools.includes(tool)
        );

        // Ensure we always have at least web_search
        if (!filteredTools.includes("web_search")) {
          filteredTools.unshift("web_search");
        }

        // Pass the generated assistant data to the parent component
        onAssistantGenerated({
          name: validatedData.name,
          description: validatedData.description,
          instructions: validatedData.instructions,
          tools: filteredTools,
          reasoning: validatedData.reasoning,
          prompt: userQuery,
          model: recommendedModel,
          primary_purpose: validatedData.primary_purpose,
          workspace_id: personalWorkspaceId,
        });
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error generating assistant:", error);
        toast({
          title: "Generation Failed",
          description:
            error.message || "Failed to generate assistant. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      generateAssistant();
    }
  };

  return (
    <div className="p-3">
      <div className="relative flex items-center">
        <Input
          placeholder="What kind of assistant do you want?"
          value={userQuery}
          onChange={(e) => setUserQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isGenerating}
          className="pr-10 h-10 rounded-full border-gray-300/50 dark:border-gray-600/50 bg-white/5 dark:bg-zinc-800/30 text-sm placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:ring-1 focus-visible:ring-gray-400/50"
        />
        <Button
          size="icon"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            generateAssistant();
          }}
          disabled={isGenerating || !userQuery.trim()}
          className="absolute right-1 h-8 w-8 rounded-full bg-gray-200/80 dark:bg-gray-700/80 hover:bg-gray-300/80 dark:hover:bg-gray-600/80 disabled:opacity-40"
        >
          {isGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-600 dark:text-gray-300" />
          ) : (
            <ArrowUp className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          )}
        </Button>
      </div>

      {/* Cancel button that's visually hidden but available for keyboard navigation */}
      <button onClick={onCancel} className="sr-only" aria-label="Cancel">
        Cancel
      </button>
    </div>
  );
};

AssistantCreationChat.propTypes = {
  onAssistantGenerated: PropTypes.func.isRequired,
  onStreamingUpdate: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
};

export default AssistantCreationChat;
